import useMerchandiseCategories from 'hooks/Merchandise/useMerchandiseCategories'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import { capitalise } from 'lib/string/stringUtils'
import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { getMerchandiseFiltersFromQuery } from './merchandiseSearchPageUtils'
import MerchandiseBreadcrumbsSection from '../MerchandiseBreadcrumbsSection'
import { getMerchandiseCategorySearchUrl } from '../merchandiseUtils'
import { take } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'

interface BreadCrumb {
  label: string
  to?: string
}

function MerchandiseSearchPageBreadcrumbs() {
  const intl = useIntl()
  const location = useLocation()
  const { categorySlug } = useParams<{ parentCategorySlug?: string, categorySlug?: string }>()
  const urlFilters = useMemo(() => getMerchandiseFiltersFromQuery(location.search), [location.search])

  const [category, fetchingCategory] = useMerchandiseCategory({ slug: categorySlug })
  const [ancestorCategories, fetchingAncestorCategories] = useMerchandiseCategories({ categoryIds: category?.ancestors.map(ancestor => ancestor.id) })

  const fetchingCategoryData = fetchingCategory || fetchingAncestorCategories

  const breadCrumbs = useMemo<Array<BreadCrumb>>(() => {
    const crumbs: Array<BreadCrumb> = []
    if (!categorySlug) {
      if (urlFilters.name) {
        crumbs.push({
          label: intl.formatMessage(
            {
              id: 'ew0wzqbN58ODIAVRGGyj',
              defaultMessage: 'Search results for "{searchTerm}"',
              description: 'Breadcrumb label for search results with search term',
            },
            { searchTerm: urlFilters.name },
          ),
        })
      } else {
        crumbs.push({
          label: intl.formatMessage({
            id: 'TNzGFfreZvBb16_UDFIi',
            defaultMessage: 'Search results',
            description: 'Breadcrumb label for search results in the search and discovery interface',
          }),
        })
      }
      return crumbs
    } else if (category) {
      // We don't want the list of categories getting too long
      // so at most we'll the highest 2 ancestors
      take(ancestorCategories.toReversed(), 2).forEach(ancestor => {
        crumbs.push({
          label: capitalise(ancestor.name),
          to: getMerchandiseCategorySearchUrl(ancestor),
        })
      })
      crumbs.push({
        label: category.name,
      })
    } else if (categorySlug && !category && !fetchingCategory) {
      // Category slug exists but category not found (invalid slug)
      crumbs.push({
        label: intl.formatMessage({
          id: 'f4uOmz-BWhqSUMOWSDoj',
          defaultMessage: 'Search results',
          description: 'Breadcrumb label for search results when category not found',
        }),
      })
    }

    return crumbs
  }, [ancestorCategories, category, categorySlug, urlFilters.name, fetchingCategory, intl])

  return <MerchandiseBreadcrumbsSection
    breadcrumbs={breadCrumbs}
    loading={fetchingCategoryData}
    alwaysExpand
  />
}

export default MerchandiseSearchPageBreadcrumbs
